import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Translate } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const COPY = {
  "pt-BR": {
    translate: "Traduzir",
    loading: "Traduzindo…",
    label: "Tradução",
    hide: "Ocultar tradução",
    error: "Não foi possível traduzir esta pergunta. Tente novamente.",
  },
  "es-419": {
    translate: "Traducir",
    loading: "Traduciendo…",
    label: "Traducción",
    hide: "Ocultar traducción",
    error: "No se pudo traducir esta pregunta. Inténtelo de nuevo.",
  },
  "fr-CA": {
    translate: "Traduire",
    loading: "Traduction…",
    label: "Traduction",
    hide: "Masquer la traduction",
    error: "Impossible de traduire cette question. Réessayez.",
  },
};

export const DOWNLOAD_COPY = {
  en: {
    reportError: "Could not download the report. Please try again.",
  },
  "pt-BR": {
    reportError: "Não foi possível baixar o relatório. Tente novamente.",
  },
  "es-419": {
    reportError: "No se pudo descargar el informe. Inténtelo de nuevo.",
  },
  "fr-CA": {
    reportError: "Impossible de télécharger le rapport. Veuillez réessayer.",
  },
};

function normalizeLocale(value) {
  const raw = String(value || "en").toLowerCase();
  if (raw === "pt" || raw === "pt-br" || raw === "pt_br") return "pt-BR";
  if (raw === "es" || raw === "es-419" || raw === "es_419") return "es-419";
  if (raw === "fr" || raw === "fr-ca" || raw === "fr_ca") return "fr-CA";
  return "en";
}

function savePdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Optional practice aid layered onto the live interview question.
 *
 * The English question remains the authoritative interview prompt. Translation
 * is requested only when the user explicitly clicks/taps the Translate button;
 * pointer hover must never reveal or request a translation. The backend caches
 * the result for that question once requested.
 *
 * This component is mounted app-wide, so it also keeps authenticated report PDF
 * downloads inside the current browser credential context. That is essential in
 * the isolated Responsive Preview Lab, where opening a raw API URL in a new tab
 * would lose the test-user session cookie.
 */
// Translate for a follow-up question (FOLLOW_UP_PLAN.md D5): the same button and
// panel as for the main question, placed by the interview page under the follow-up.
export function FollowUpTranslation({ sessionId, questionIndex, followUpId }) {
  const { user } = useAuth();
  const copy = COPY[normalizeLocale(user?.preferred_language)];
  const [translation, setTranslation] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTranslation("");
    setOpen(false);
    setError("");
  }, [sessionId, questionIndex, followUpId]);

  if (!copy || !followUpId) return null;

  const toggle = async () => {
    if (translation && open) {
      setOpen(false);
      return;
    }
    if (translation) {
      setOpen(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("question_index", String(questionIndex));
      fd.append("follow_up_id", followUpId);
      const { data } = await api.post(`/sessions/${sessionId}/question-translation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const translated = String(data?.translation || "").trim();
      if (!translated) throw new Error("empty translation");
      setTranslation(translated);
    } catch {
      setError(copy.error);
    } finally {
      setOpen(true);
      setLoading(false);
    }
  };

  return (
    <div className="mt-3" data-testid="follow-up-translation-aid">
      <button
        type="button"
        data-testid="translate-follow-up-btn"
        onClick={toggle}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--brand)]/5 hover:text-[color:var(--brand)] px-4 py-2 text-sm disabled:opacity-50"
        aria-expanded={open}
      >
        <Translate size={16} />
        {loading ? copy.loading : translation && open ? copy.hide : copy.translate}
      </button>
      {open && (
        <div
          className="mt-3 max-w-3xl rounded-2xl border border-[color:var(--brand)]/25 bg-[color:var(--brand)]/5 p-4"
          role="note"
          data-testid="follow-up-translation"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand)]">{copy.label}</div>
          {error ? (
            <p className="mt-2 text-sm text-[color:var(--score-bad)]">{error}</p>
          ) : (
            <p className="mt-2 text-base leading-relaxed text-[color:var(--text-primary)]">{translation}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuestionTranslationAid() {
  const location = useLocation();
  const { user } = useAuth();
  const locale = normalizeLocale(user?.preferred_language);
  const copy = COPY[locale];
  const match = location.pathname.match(/^\/interview\/([^/]+)$/);
  const sessionId = match?.[1] || null;

  const [host, setHost] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(null);
  const [translation, setTranslation] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionQuestions, setSessionQuestions] = useState([]);

  const cacheKey = useMemo(
    () => (sessionId != null && questionIndex != null ? `${sessionId}:${questionIndex}:${locale}` : null),
    [sessionId, questionIndex, locale]
  );

  // The report link historically opened the raw authenticated API URL in a new
  // tab. That breaks inside a credentialless Preview Lab iframe because the popup
  // does not inherit the isolated test-user cookie. Intercept the download control
  // and fetch the PDF through the authenticated Axios client
  // in the current context, then hand a local Blob to the browser as a download.
  useEffect(() => {
    const reportMatch = location.pathname.match(/^\/report\/([^/]+)$/);
    const reportSessionId = reportMatch?.[1] || null;
    if (!reportSessionId || typeof document === "undefined") return undefined;

    let busy = false;
    const messages = DOWNLOAD_COPY[locale] || DOWNLOAD_COPY.en;

    const onDownloadClick = (event) => {
      const target = event.target?.closest?.('[data-testid="download-report-pdf"]');
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      busy = true;

      target.setAttribute("aria-busy", "true");
      target.classList.add("opacity-70", "cursor-wait");

      void (async () => {
        try {
          const response = await api.get(`/sessions/${reportSessionId}/report.pdf`, { responseType: "blob" });
          savePdfBlob(response.data, `Language-Masters-Interview-Report-${reportSessionId}.pdf`);
        } catch {
          toast.error(messages.reportError);
        } finally {
          busy = false;
          target.removeAttribute("aria-busy");
          target.classList.remove("opacity-70", "cursor-wait");
        }
      })();
    };

    document.addEventListener("click", onDownloadClick, true);
    return () => document.removeEventListener("click", onDownloadClick, true);
  }, [location.pathname, locale]);

  // Load only the question list needed to map the visible English prompt to its
  // stable index. This does not create or request any translations.
  useEffect(() => {
    if (!sessionId || !copy) {
      setSessionQuestions([]);
      return undefined;
    }
    let alive = true;
    api.get(`/sessions/${sessionId}`)
      .then(({ data }) => {
        if (alive) setSessionQuestions(data?.questions || []);
      })
      .catch(() => {
        if (alive) setSessionQuestions([]);
      });
    return () => { alive = false; };
  }, [sessionId, copy]);

  // The interview page intentionally owns the question layout. Add a tiny host
  // immediately after the visible question rather than duplicating that page's
  // interview state. MutationObserver handles question changes and reveal-text
  // mode without polling.
  useEffect(() => {
    if (!sessionId || !copy || typeof document === "undefined") {
      setHost(null);
      return undefined;
    }

    let currentHeading = null;
    let currentHost = null;

    const sync = () => {
      const heading = document.querySelector('[data-testid="question-text"]');
      if (!heading) {
        setHost(null);
        setQuestionIndex(null);
        return;
      }

      const visible = (heading.textContent || "").trim();
      const idx = sessionQuestions.findIndex(
        (q) => String(q?.text || "").trim() === visible
      );
      if (idx < 0) return;

      if (heading !== currentHeading) {
        if (currentHost?.parentNode) currentHost.parentNode.removeChild(currentHost);
        currentHost = document.createElement("div");
        currentHost.dataset.questionTranslationAid = "true";
        heading.insertAdjacentElement("afterend", currentHost);
        currentHeading = heading;
        setHost(currentHost);
      }
      setQuestionIndex(idx);
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      if (currentHost?.parentNode) currentHost.parentNode.removeChild(currentHost);
      setHost(null);
    };
  }, [sessionId, copy, sessionQuestions]);

  useEffect(() => {
    setTranslation("");
    setOpen(false);
    setError("");
  }, [cacheKey]);

  const requestTranslation = useCallback(async ({ reveal = true } = {}) => {
    if (!sessionId || questionIndex == null || !copy) return;
    if (translation) {
      if (reveal) setOpen(true);
      return;
    }
    if (loading) return;

    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("question_index", String(questionIndex));
      const { data } = await api.post(
        `/sessions/${sessionId}/question-translation`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const translated = String(data?.translation || "").trim();
      if (!translated) throw new Error("empty translation");
      setTranslation(translated);
      if (reveal) setOpen(true);
    } catch {
      setError(copy.error);
      if (reveal) setOpen(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId, questionIndex, copy, translation, loading]);

  if (!host || !copy || questionIndex == null) return null;

  return createPortal(
    <div className="mt-4" data-testid="question-translation-aid">
      <button
        type="button"
        data-testid="translate-question-btn"
        onClick={() => {
          if (translation && open) {
            setOpen(false);
          } else {
            void requestTranslation({ reveal: true });
          }
        }}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--brand)]/5 hover:text-[color:var(--brand)] px-4 py-2 text-sm disabled:opacity-50"
        aria-expanded={open}
      >
        <Translate size={16} />
        {loading ? copy.loading : translation && open ? copy.hide : copy.translate}
      </button>

      {open && (
        <div
          className="mt-3 max-w-3xl rounded-2xl border border-[color:var(--brand)]/25 bg-[color:var(--brand)]/5 p-4"
          role="note"
          data-testid="question-translation"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand)]">
            {copy.label}
          </div>
          {error ? (
            <p className="mt-2 text-sm text-[color:var(--score-bad)]">{error}</p>
          ) : (
            <p className="mt-2 text-base leading-relaxed text-[color:var(--text-primary)]">
              {translation}
            </p>
          )}
        </div>
      )}
    </div>,
    host
  );
}
